import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast } from "@/components/podkit/toasts/use-toast";
import {
    getExperiment,
    getPendingExperiment,
    setPendingExperiment,
    clearPendingExperiment,
    useExperimentsEnabled,
    useExperimentMigration,
} from "@/hooks/use-experiment";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useEffect, useState, type FC } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

/**
 * Captures experiment name from URL and stores it in localStorage.
 *
 * WHY MODULE-LEVEL EXECUTION:
 * This must run at module load time (before React renders) because:
 * 1. User visits /experiments/ralph
 * 2. AuthenticatedUserLayout checks auth → redirects to /login
 * 3. After login → may redirect to /onboarding
 * 4. After onboarding → redirects to / (losing the original URL)
 *
 * By capturing at module load, we store the intent before any redirects.
 * The PendingExperimentActivation component then processes it after auth.
 */
function captureExperimentFromUrl() {
    if (typeof window === "undefined") return;

    const regex = /^\/experiments\/([^/]+)/;
    const match = regex.exec(window.location.pathname);
    if (match?.[1]) {
        const experimentName = match[1];
        const experiment = getExperiment(experimentName);
        if (experiment) {
            setPendingExperiment(experimentName);
        }
    }
}

captureExperimentFromUrl();

function showExperimentsEnabledToast(experimentName: string, experimentDisplayName: string) {
    if (experimentName === "ralph") {
        toast({
            title: (
                <span className="flex items-center gap-2">
                    <IconRalphWiggum size={24} />
                    <span>Experiments enabled!</span>
                </span>
            ),
            description: "You can manage experiments in Settings → Experiments",
        });
    } else {
        toast({
            title: `Experiments enabled via ${experimentDisplayName}!`,
            description: "You can manage experiments in Settings → Experiments",
        });
    }
}

/**
 * Page rendered at /experiments/:name.
 * Enables the global experiments toggle and redirects to /.
 */
export const ExperimentsActivationPage: FC = () => {
    const { name } = useParams<{ name: string }>();
    const navigate = useNavigate();
    const { enabled, setEnabled, loading } = useExperimentsEnabled();
    const [hasActivated, setHasActivated] = useState(false);
    const segmentTrack = useSegmentTrack();

    const experiment = name ? getExperiment(name) : null;

    useEffect(() => {
        if (loading) return;

        if (!experiment && name) {
            toast({
                title: "Experiment not found",
                description: `The experiment "${name}" doesn't exist or is no longer available.`,
            });
            navigate("/", { replace: true });
            return;
        }

        if (experiment && !hasActivated && name) {
            if (!enabled) {
                setEnabled(true);
            }
            setHasActivated(true);

            segmentTrack("experiment_enabled", {
                experiment_name: name,
                experiment_display_name: experiment.name,
                source: "activation_link",
            });

            showExperimentsEnabledToast(name, experiment.name);
            navigate("/", { replace: true });
        }
    }, [experiment, enabled, setEnabled, hasActivated, loading, name, navigate, segmentTrack]);

    if (hasActivated) {
        return <Navigate to="/" replace />;
    }

    return <LoadingState />;
};

/**
 * Processes any pending experiment activation after login/onboarding.
 * Also runs the one-time migration from the legacy experiment system.
 * Render this in the main app after authentication is confirmed.
 */
export const PendingExperimentActivation: FC = () => {
    const { enabled, setEnabled } = useExperimentsEnabled();
    const segmentTrack = useSegmentTrack();

    // Run one-time migration to clean up legacy feature_flag_* keys
    useExperimentMigration();

    useEffect(() => {
        const pending = getPendingExperiment();
        if (!pending) return;

        const experiment = getExperiment(pending);
        if (!experiment) {
            clearPendingExperiment();
            return;
        }

        // Clear first to prevent re-fire when setEnabled triggers a re-render
        clearPendingExperiment();

        if (!enabled) {
            setEnabled(true);
        }

        segmentTrack("experiment_enabled", {
            experiment_name: pending,
            experiment_display_name: experiment.name,
            source: "activation_link_after_auth",
        });

        showExperimentsEnabledToast(pending, experiment.name);
    }, [enabled, setEnabled, segmentTrack]);

    return null;
};
